// src/components/ReportBlockMenu.tsx
// Bouton "Signaler" (+ "Bloquer l'auteur") réutilisable pour tout contenu généré
// par les utilisateurs (App Store Guideline 1.2). Autonome : gère sa propre modale,
// pas de dépendance à un namespace i18n particulier (texte FR/EN embarqué).

import { useState } from "react";
import { Flag, UserX, X } from "lucide-react";
import i18n from "../i18n";
import {
  reportContent,
  blockUser,
  REPORT_REASONS,
  type ContentType,
  type ReportReason,
} from "../lib/moderation";

type Props = {
  contentType: ContentType;
  contentId: string;
  authorId?: string | null;
  authorName?: string | null;
  currentUserId?: string | null;
  onBlocked?: (authorId: string) => void;
  className?: string;
};

const REASON_LABEL: Record<ReportReason, { fr: string; en: string }> = {
  inappropriate: { fr: "Contenu inapproprié", en: "Inappropriate content" },
  harassment: { fr: "Harcèlement / propos haineux", en: "Harassment / hate speech" },
  spam: { fr: "Spam / publicité", en: "Spam / advertising" },
  other: { fr: "Autre", en: "Other" },
};

function useLang(): "fr" | "en" {
  const l = (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  return l.startsWith("en") ? "en" : "fr";
}

export function ReportBlockMenu({
  contentType,
  contentId,
  authorId,
  authorName,
  currentUserId,
  onBlocked,
  className,
}: Props) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>("inappropriate");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const isSelf = !!authorId && !!currentUserId && authorId === currentUserId;
  if (isSelf) return null;

  const close = () => {
    setOpen(false);
    setDone(false);
    setErr("");
    setDetails("");
  };

  const submitReport = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await reportContent(contentType, contentId, authorId ?? null, reason, details);
      setDone(true);
    } catch {
      setErr(lang === "fr" ? "Échec de l'envoi. Réessaie." : "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlock = async () => {
    if (!authorId) return;
    const label = authorName || (lang === "fr" ? "cet utilisateur" : "this user");
    const msg =
      lang === "fr"
        ? `Bloquer ${label} ? Tu ne verras plus ses contenus.`
        : `Block ${label}? You will no longer see their content.`;
    if (!window.confirm(msg)) return;
    try {
      await blockUser(authorId);
      onBlocked?.(authorId);
      close();
    } catch {
      alert(lang === "fr" ? "Échec du blocage." : "Failed to block.");
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={lang === "fr" ? "Signaler" : "Report"}
        aria-label={lang === "fr" ? "Signaler ce contenu" : "Report this content"}
        className="inline-flex items-center justify-center rounded-full p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-950/30 transition-colors"
      >
        <Flag className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {lang === "fr" ? "Signaler ce contenu" : "Report this content"}
              </h3>
              <button
                type="button"
                onClick={close}
                aria-label={lang === "fr" ? "Fermer" : "Close"}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <p className="mt-4 text-sm text-green-700 dark:text-green-400">
                {lang === "fr"
                  ? "Merci, ton signalement a été envoyé et sera examiné rapidement."
                  : "Thanks, your report has been sent and will be reviewed shortly."}
              </p>
            ) : (
              <>
                <div className="mt-3 space-y-2">
                  {REPORT_REASONS.map((r) => (
                    <label
                      key={r}
                      className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
                    >
                      <input
                        type="radio"
                        name={`report-reason-${contentId}`}
                        checked={reason === r}
                        onChange={() => setReason(r)}
                      />
                      {REASON_LABEL[r][lang]}
                    </label>
                  ))}
                </div>

                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={2}
                  placeholder={lang === "fr" ? "Précisions (optionnel)" : "Details (optional)"}
                  className="mt-3 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                />

                {err && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{err}</p>}

                <div className="mt-4 flex items-center justify-between gap-2">
                  {authorId && (
                    <button
                      type="button"
                      onClick={handleBlock}
                      className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      <UserX className="h-4 w-4" />
                      {lang === "fr" ? "Bloquer l'auteur" : "Block author"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={submitReport}
                    disabled={submitting}
                    className="ml-auto inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-3 py-2 text-sm font-semibold transition-colors"
                  >
                    <Flag className="h-4 w-4" />
                    {submitting
                      ? lang === "fr"
                        ? "Envoi…"
                        : "Sending…"
                      : lang === "fr"
                        ? "Signaler"
                        : "Report"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportBlockMenu;
